import { Controller } from "@hotwired/stimulus"
import { createCalendar, viewMonthGrid, viewWeek, viewMonthAgenda } from "@schedule-x/calendar"
import { createDragAndDropPlugin } from "@schedule-x/drag-and-drop"

// Renders the admin "Calendar" framing of the Ideas index (schedule-x).
// Unaccepted ideas with a target publish date plot on the grid in the
// primary color; accepted/published ideas plot muted and read-only.
//
// The side tray (rendered by the Rails _calendar_tray partial) holds only
// ideas planning has bucketed into the *currently visible month* that still
// need a publish date, grouped by hub. As the admin pages the grid to another
// month, this controller refetches the tray for that month. Dragging a tray
// card onto a day PATCHes the idea's target_date, drops it out of the tray,
// and plots it as an event without a full page reload. Dragging an event onto
// the "clear" drop zone nulls the publish date back out, returning the idea to
// its delivery month's tray. All of this is deliberately independent of
// workorder_month (the delivery/billing month), which the Plan grid owns.
const RECIPE_FILTER_KEY = "ideas-plan:recipe-filter"

export default class extends Controller {
  static targets = ["calendar", "trayContainer", "clearDropZone", "trayCard", "trayEmptyMessage"]
  static values = {
    events: Array,
    updateUrlTemplate: String,
    trayUrl: String,
    trayMonth: String,
    recipeFilter: { type: String, default: "all" }
  }

  connect() {
    this.restoreRecipeFilter()

    // Local, mutable copy of the serialized events. schedule-x holds its own
    // set; we keep this in sync so the recipe toggle can re-derive the visible
    // set and so drops/clears can add and remove without a page reload.
    this.events = [...this.eventsValue]
    this.currentTrayMonth = this.trayMonthValue

    // Which hub section (by hub key) is expanded in the tray. Sections default
    // closed and only one opens at a time; we hold the choice here so it
    // survives the tray re-render that follows a drag.
    this.openHubKey = null

    this.calendar = createCalendar({
      views: [viewMonthGrid, viewWeek, viewMonthAgenda],
      defaultView: viewMonthGrid.name,
      calendars: {
        unaccepted: {
          colorName: "unaccepted",
          lightColors: { main: "#0a8484", container: "#e6f4f4", onContainer: "#075a5a" }
        },
        accepted: {
          colorName: "accepted",
          lightColors: { main: "#9CA3AF", container: "#F3F4F6", onContainer: "#6B7280" }
        },
        recipe_unaccepted: {
          colorName: "recipe_unaccepted",
          lightColors: { main: "#026565", container: "#e7f3f3", onContainer: "#075a5a" }
        }
      },
      events: this.filteredEvents(),
      plugins: [createDragAndDropPlugin()],
      callbacks: {
        onEventUpdate: (event) => this.handleEventMoved(event),
        onEventClick: (event) => this.handleEventClick(event),
        onRangeUpdate: (range) => this.handleRangeUpdate(range)
      }
    })

    this.calendar.render(this.calendarTarget)
    this.setupTrayDropTarget()
    this.setupClearDropZone()
    this.updateSegmentState()
    this.applyRecipeFilterToTray()
    this.applyTrayOpenState()
  }

  disconnect() {
    if (this.calendar) {
      this.calendar.destroy()
      this.calendar = null
    }
  }

  handleEventClick(event) {
    if (event.href) {
      window.location.href = event.href
    }
  }

  // ── Month navigation ────────────────────────────────────────────────────────
  //
  // schedule-x pages the grid client-side. When the visible month changes we
  // pull the tray for the new month so it only ever lists ideas whose delivery
  // month matches what the admin is looking at.

  handleRangeUpdate(range) {
    const month = this.monthFromRange(range)
    if (!month || month === this.currentTrayMonth) return

    this.currentTrayMonth = month
    this.refreshTray(month)
  }

  // The focal month of a schedule-x range. A month grid's range spans the
  // leading/trailing days of the neighbouring months, so the midpoint of the
  // range reliably lands in the month actually being viewed.
  monthFromRange(range) {
    if (!range || !range.start || !range.end) return null

    const start = new Date(`${String(range.start).slice(0, 10)}T00:00:00`)
    const end = new Date(`${String(range.end).slice(0, 10)}T00:00:00`)
    if (isNaN(start) || isNaN(end)) return null

    const midpoint = new Date((start.getTime() + end.getTime()) / 2)
    return `${midpoint.getFullYear()}-${String(midpoint.getMonth() + 1).padStart(2, "0")}`
  }

  async refreshTray(month) {
    if (!this.hasTrayContainerTarget || !this.hasTrayUrlValue) return

    try {
      const url = `${this.trayUrlValue}?cal_month=${encodeURIComponent(month)}`
      const response = await fetch(url, {
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" }
      })
      if (!response.ok) return

      this.trayContainerTarget.innerHTML = await response.text()
      this.applyRecipeFilterToTray()
      this.applyTrayOpenState()
    } catch (error) {
      console.error("Failed to refresh calendar tray", error)
    }
  }

  // ── Hub accordion ────────────────────────────────────────────────────────────
  //
  // Sections default closed; opening one closes any other. Managed here (rather
  // than Bootstrap's collapse data-api) so the open section is remembered and
  // reapplied after the tray HTML is swapped out by a month change or a drop.

  toggleHub(event) {
    const group = event.currentTarget.closest(".idea-tray-group")
    const key = group?.dataset.hubKey
    if (!key) return

    this.openHubKey = this.openHubKey === key ? null : key
    this.applyTrayOpenState()
  }

  applyTrayOpenState() {
    if (!this.hasTrayContainerTarget) return

    this.trayContainerTarget.querySelectorAll(".idea-tray-group").forEach((group) => {
      const isOpen = group.dataset.hubKey === this.openHubKey
      const panel = group.querySelector(".idea-tray-group__panel")
      const toggle = group.querySelector(".idea-tray-group__head")

      panel?.classList.toggle("show", isOpen)
      toggle?.classList.toggle("idea-tray-group__head--open", isOpen)
      toggle?.setAttribute("aria-expanded", isOpen ? "true" : "false")
    })
  }

  // ── Recipe / Article toggle ─────────────────────────────────────────────────
  //
  // The segmented control lives inside this controller's element (see
  // _calendar.html.erb) so its click target resolves here. It re-filters the
  // pre-serialized event set schedule-x already has rather than a server
  // round trip, and hides the matching tray cards the same way the Plan
  // view's rail does. Shares session storage with the Plan view's toggle so
  // flipping it on one board carries to the other.

  restoreRecipeFilter() {
    if (this.hasRecipeFilterValue) {
      this.persistRecipeFilter(this.recipeFilterValue)
      return
    }

    try {
      const saved = sessionStorage.getItem(RECIPE_FILTER_KEY)
      if (saved) this.recipeFilterValue = saved
    } catch (error) {
      // Private browsing and blocked storage both throw here.
    }
  }

  persistRecipeFilter(kind) {
    try {
      sessionStorage.setItem(RECIPE_FILTER_KEY, kind)
    } catch (error) {
      // See restoreRecipeFilter.
    }
  }

  selectRecipeFilter(event) {
    event.preventDefault()
    const kind = event.currentTarget.dataset.kind || "all"

    this.recipeFilterValue = kind
    this.persistRecipeFilter(kind)
    this.updateSegmentState()
    this.calendar?.events.set(this.filteredEvents())
    this.applyRecipeFilterToTray()
  }

  updateSegmentState() {
    this.element.querySelectorAll(".fg-planbar__segmentbtn").forEach((button) => {
      button.classList.toggle("fg-planbar__segmentbtn--on", (button.dataset.kind || "all") === this.recipeFilterValue)
    })
  }

  matchesRecipeFilter(kind) {
    if (this.recipeFilterValue === "recipes") return kind === "recipe"
    if (this.recipeFilterValue === "articles") return kind === "article"

    return true
  }

  filteredEvents() {
    return this.events.filter((event) => this.matchesRecipeFilter(event.kind))
  }

  applyRecipeFilterToTray() {
    this.trayCardTargets.forEach((card) => {
      const isRecipe = card.dataset.isRecipe === "true"
      card.hidden = !this.matchesRecipeFilter(isRecipe ? "recipe" : "article")
    })
  }

  // ── Drag and drop ────────────────────────────────────────────────────────────

  handleEventMoved(event) {
    this.updateLocalEvent(event.id, { start: event.start, end: event.start })
    this.persistPublishDate(event.id, event.start)
  }

  setupTrayDropTarget() {
    this.calendarTarget.addEventListener("dragover", (e) => e.preventDefault())
    this.calendarTarget.addEventListener("drop", async (e) => {
      e.preventDefault()
      const ideaId = e.dataTransfer.getData("text/idea-id")
      if (!ideaId) return

      const isoDate = this.dropTargetDate(e) || `${this.currentTrayMonth}-01`
      const card = this.trayCardTargets.find((el) => el.dataset.ideaId === ideaId)

      const data = await this.persistPublishDate(ideaId, isoDate)
      if (!data || !data.ok) return

      // Plot it immediately, then refetch the tray so hub counts and the empty
      // state settle from the server's own grouping rather than DOM surgery.
      this.addLocalEvent(ideaId, isoDate, card)
      this.refreshTray(this.currentTrayMonth)
    })
  }

  // Best-effort day detection. schedule-x annotates month-grid day cells with
  // data-date-grid-date (week/day views use data-time-grid-date). Fall back to
  // a bare data-date, and finally to the first of the visible month if the
  // drop didn't land on a recognizable cell.
  dropTargetDate(e) {
    const dropTarget = document.elementFromPoint(e.clientX, e.clientY)
    if (!dropTarget) return null

    const cell = dropTarget.closest("[data-date-grid-date], [data-time-grid-date], [data-date]")
    if (!cell) return null

    const raw =
      cell.getAttribute("data-date-grid-date") ||
      cell.getAttribute("data-time-grid-date") ||
      cell.getAttribute("data-date")

    return raw ? String(raw).slice(0, 10) : null
  }

  setupClearDropZone() {
    if (!this.hasClearDropZoneTarget) return

    this.clearDropZoneTarget.addEventListener("dragover", (e) => e.preventDefault())
    this.clearDropZoneTarget.addEventListener("drop", async (e) => {
      e.preventDefault()
      const ideaId = e.dataTransfer.getData("text/idea-id")
      if (!ideaId) return

      const data = await this.persistPublishDate(ideaId, null)
      if (!data || !data.ok) return

      // The idea no longer has a publish date, so drop the event and pull the
      // tray again: if it belongs to the visible delivery month it returns.
      this.removeLocalEvent(ideaId)
      this.refreshTray(this.currentTrayMonth)
    })
  }

  trayDragStart(event) {
    const ideaId = event.currentTarget.dataset.ideaId
    event.dataTransfer.setData("text/idea-id", ideaId)
    event.dataTransfer.effectAllowed = "move"
  }

  // ── Local event set bookkeeping ───────────────────────────────────────────────
  //
  // Keep this.events (the filter's source of truth) and schedule-x's own set in
  // step as drops and clears mutate the board.

  addLocalEvent(ideaId, isoDate, card) {
    const id = Number(ideaId)
    const isRecipe = card?.dataset.isRecipe === "true"

    const event = {
      id,
      title: card?.dataset.title || "Untitled idea",
      start: isoDate,
      end: isoDate,
      calendarId: isRecipe ? "recipe_unaccepted" : "unaccepted",
      kind: isRecipe ? "recipe" : "article",
      href: card?.dataset.href || ""
    }

    this.events = this.events.filter((e) => e.id !== id).concat(event)
    this.calendar?.events.set(this.filteredEvents())
  }

  updateLocalEvent(ideaId, changes) {
    const id = Number(ideaId)
    this.events = this.events.map((e) => (e.id === id ? { ...e, ...changes } : e))
  }

  removeLocalEvent(ideaId) {
    const id = Number(ideaId)
    this.events = this.events.filter((e) => e.id !== id)
    this.calendar?.events.set(this.filteredEvents())
  }

  async persistPublishDate(ideaId, isoDate) {
    const url = this.updateUrlTemplateValue.replace(":id", ideaId)

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
        },
        body: JSON.stringify({ idea: { target_date: isoDate } })
      })

      const data = await response.json()
      if (!data.ok) console.error("Failed to update idea publish date", data.errors)
      return data
    } catch (error) {
      console.error("Failed to update idea publish date", error)
      return null
    }
  }
}
